/**
 * Central analytics module — single source of truth for all fbq and
 * dataLayer calls throughout the app.
 *
 * Design principles:
 *  - Every function is a no-op if the tracking script is blocked or hasn't loaded.
 *  - UTM params and click-IDs (fbclid, gclid) are captured synchronously in
 *    index.html and stored in sessionStorage for first-touch attribution.
 *    They are attached to every conversion event pushed to the dataLayer.
 *  - No direct fbq() or dataLayer.push() calls outside this module.
 */

// ─── UTM / Click-ID Preservation ─────────────────────────────────────────────

const UTM_SESSION_KEY = '_visto_utm';
const UTM_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign',
  'utm_content', 'utm_term', 'fbclid', 'gclid', 'ttclid',
] as const;

/**
 * Capture UTM params and click-IDs from the current URL into sessionStorage.
 * Called as a safety net from the React tree; the primary capture runs
 * synchronously in index.html before React hydrates.
 * No-op if already captured (preserves first-touch attribution).
 */
export function captureUTMs(): void {
  if (typeof window === 'undefined') return;
  try {
    if (sessionStorage.getItem(UTM_SESSION_KEY)) return;
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    let hasAny = false;
    UTM_KEYS.forEach((key) => {
      const val = params.get(key);
      if (val) { utm[key] = val; hasAny = true; }
    });
    if (hasAny) sessionStorage.setItem(UTM_SESSION_KEY, JSON.stringify(utm));
  } catch { /* sessionStorage blocked (private mode edge case) */ }
}

/** Returns the first-touch UTM attribution stored for this tab session. */
export function getStoredUTMs(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(UTM_SESSION_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
}

// ─── Low-level safe wrappers ──────────────────────────────────────────────────

/**
 * Safe Meta Pixel call.
 * Works whether the real fbevents.js has loaded or is still in the queue
 * (the stub in index.html queues events and replays them on load).
 * Silently skips if fbq is blocked entirely.
 */
function callFbq(eventType: string, eventName: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  const fbq = (window as any).fbq;
  if (typeof fbq !== 'function') return;
  fbq(eventType, eventName, params);
}

/**
 * Safe dataLayer push.
 * window.dataLayer is pre-initialized synchronously in index.html, so this
 * call is always safe regardless of when GA4/GTM loads.
 */
function pushDataLayer(data: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push(data);
}

// ─── Tracking events ──────────────────────────────────────────────────────────

/**
 * Fire a virtual PageView on SPA route changes.
 *
 * The initial page load PageView is handled by:
 *   - Meta Pixel: fbq('track', 'PageView') in index.html (deferred)
 *   - GA4:        gtag('config', ..., { send_page_view: true }) in index.html
 *
 * This function should be called for every SUBSEQUENT route change.
 * The useRouteTracking hook skips the first render to avoid double-firing.
 */
export function trackPageView(path: string): void {
  callFbq('track', 'PageView');
  pushDataLayer({
    event: 'page_view',
    page_path: path,
    page_title: typeof document !== 'undefined' ? document.title : '',
  });
}

/**
 * A user selected a business from the hero search widget.
 *
 * This is the top-of-funnel "Lead" signal — the user has identified
 * themselves as a potential customer with a specific business in mind.
 *
 * Meta standard event: Lead
 * GA4 / GTM custom event: generate_lead
 */
export function trackLead(businessName: string): void {
  callFbq('track', 'Lead');
  pushDataLayer({
    event: 'generate_lead',
    lead_type: 'business_search',
    business_name: businessName,
    ...getStoredUTMs(),
  });
}

/**
 * A user successfully completed registration.
 * Called after Firestore documents are created and refreshUser() resolves.
 *
 * Meta standard event: CompleteRegistration
 * GA4 / GTM standard event: sign_up (maps to Google Ads conversion)
 *
 * @param method  'email' for email/password signup, 'google' for OAuth
 */
export function trackCompleteRegistration(method: 'email' | 'google'): void {
  callFbq('track', 'CompleteRegistration');
  pushDataLayer({
    event: 'sign_up',
    method,
    ...getStoredUTMs(),
  });
}
